import type { components } from '@leanin/api-client';
import type { useTranslations } from '@/i18n/translations';

export type ActionKey =
  components['schemas']['RecordOnboardingActionBody']['actionKey'];

export type ChecklistItemSignal = 'derived' | 'visit';

// A catalog key resolved through the root (namespace-less) translator, so a
// step can borrow copy another feature owns — `visit_resources` describes
// the library with the Resources page's own subtitle instead of a copy that
// drifts from it.
type CatalogKey = Parameters<ReturnType<typeof useTranslations<never>>>[0];

export type StepContent = {
  readonly titleKey: CatalogKey;
  readonly subKey: CatalogKey;
  readonly ctaKey: CatalogKey;
  readonly href: string;
  readonly signal: ChecklistItemSignal;
};

// Per-action_key copy keys / CTA / link. The user's step list (an ordered
// array of action_keys) comes from the server; the frontend resolves each
// key through this map to render the card and visit-tracker. One canonical
// copy bundle per key — persona-specific copy belongs in a distinct
// action_key (e.g. `first_network_post`), not a per-persona variant here.
export const STEP_CONTENT_BY_KEY: Readonly<Record<ActionKey, StepContent>> = {
  confirm_profile: {
    titleKey: 'Onboarding.checklist.steps.confirm_profile.title',
    subKey: 'Onboarding.checklist.steps.confirm_profile.sub',
    ctaKey: 'Onboarding.checklist.steps.confirm_profile.cta',
    href: '/settings/account',
    signal: 'derived',
  },
  first_post: {
    titleKey: 'Onboarding.checklist.steps.first_post.title',
    subKey: 'Onboarding.checklist.steps.first_post.sub',
    ctaKey: 'Onboarding.checklist.steps.first_post.cta',
    // `?compose=true` auto-opens + focuses the feed composer on landing
    // (handled in the /feed route) — otherwise clicking lands on a feed the
    // user is often already viewing and "nothing happens". Boolean-shaped
    // value: string hrefs JSON-round-trip through the router, so `1`
    // arrives as the string '"1"'.
    href: '/feed?compose=true',
    signal: 'derived',
  },
  joined_circle: {
    titleKey: 'Onboarding.checklist.steps.joined_circle.title',
    subKey: 'Onboarding.checklist.steps.joined_circle.sub',
    ctaKey: 'Onboarding.checklist.steps.joined_circle.cta',
    href: '/circles',
    signal: 'derived',
  },
  joined_group: {
    titleKey: 'Onboarding.checklist.steps.joined_group.title',
    subKey: 'Onboarding.checklist.steps.joined_group.sub',
    ctaKey: 'Onboarding.checklist.steps.joined_group.cta',
    href: '/groups',
    signal: 'derived',
  },
  joined_network: {
    titleKey: 'Onboarding.checklist.steps.joined_network.title',
    subKey: 'Onboarding.checklist.steps.joined_network.sub',
    ctaKey: 'Onboarding.checklist.steps.joined_network.cta',
    href: '/networks',
    signal: 'derived',
  },
  visit_resources: {
    titleKey: 'Onboarding.checklist.steps.visit_resources.title',
    subKey: 'Resources.list.subtitle',
    ctaKey: 'Onboarding.checklist.steps.visit_resources.cta',
    href: '/resources',
    signal: 'visit',
  },
  visit_meeting_guides: {
    titleKey: 'Onboarding.checklist.steps.visit_meeting_guides.title',
    subKey: 'Onboarding.checklist.steps.visit_meeting_guides.sub',
    ctaKey: 'Onboarding.checklist.steps.visit_meeting_guides.cta',
    href: '/resources?scope=leadership_library',
    signal: 'visit',
  },
  complete_circle_profile: {
    titleKey: 'Onboarding.checklist.steps.complete_circle_profile.title',
    subKey: 'Onboarding.checklist.steps.complete_circle_profile.sub',
    ctaKey: 'Onboarding.checklist.steps.complete_circle_profile.cta',
    href: '/circles',
    // Completes when the circle actually HAS an image + description —
    // derived server-side on progress reads (get-progress self-heal), not
    // by visiting the admin screen.
    signal: 'derived',
  },
  attend_training: {
    titleKey: 'Onboarding.checklist.steps.attend_training.title',
    subKey: 'Onboarding.checklist.steps.attend_training.sub',
    ctaKey: 'Onboarding.checklist.steps.attend_training.cta',
    href: '/events?tab=platform',
    signal: 'visit',
  },
  invite_circle_members: {
    titleKey: 'Onboarding.checklist.steps.invite_circle_members.title',
    subKey: 'Onboarding.checklist.steps.invite_circle_members.sub',
    ctaKey: 'Onboarding.checklist.steps.invite_circle_members.cta',
    href: '/circles',
    signal: 'derived',
  },
  plan_first_meeting: {
    titleKey: 'Onboarding.checklist.steps.plan_first_meeting.title',
    subKey: 'Onboarding.checklist.steps.plan_first_meeting.sub',
    ctaKey: 'Onboarding.checklist.steps.plan_first_meeting.cta',
    // Patched to the environment's first-meeting guide resource (its id is
    // per-environment config, surfaced on /me/onboarding-progress). The
    // library list is the fallback when unconfigured.
    href: '/resources',
    signal: 'visit',
  },
  visit_hub: {
    titleKey: 'Onboarding.checklist.steps.visit_hub.title',
    subKey: 'Onboarding.checklist.steps.visit_hub.sub',
    ctaKey: 'Onboarding.checklist.steps.visit_hub.cta',
    href: '/networks',
    // Completes when she requests to join (or joins) the Hub — recorded by
    // the onboarding event subscriber — not by visiting the page.
    signal: 'derived',
  },
  update_network_page: {
    titleKey: 'Onboarding.checklist.steps.update_network_page.title',
    subKey: 'Onboarding.checklist.steps.update_network_page.sub',
    ctaKey: 'Onboarding.checklist.steps.update_network_page.cta',
    href: '/networks',
    // Completes when the network page actually HAS an image + description —
    // derived server-side on progress reads (get-progress self-heal), not
    // by visiting the manage screen.
    signal: 'derived',
  },
  add_event: {
    titleKey: 'Onboarding.checklist.steps.add_event.title',
    subKey: 'Onboarding.checklist.steps.add_event.sub',
    ctaKey: 'Onboarding.checklist.steps.add_event.cta',
    // The create-event form. Patched to `?groupId=<network>` when the leader
    // has a network so the event is pre-attributed to it.
    href: '/events/new',
    signal: 'derived',
  },
  invite_network_members: {
    titleKey: 'Onboarding.checklist.steps.invite_network_members.title',
    subKey: 'Onboarding.checklist.steps.invite_network_members.sub',
    ctaKey: 'Onboarding.checklist.steps.invite_network_members.cta',
    href: '/networks',
    signal: 'derived',
  },
};

// Replaces the static directory href in STEP_CONTENT_BY_KEY with a deep
// link to the viewer's actual group when the step requires leader context.
// Each "manage" step targets the group's admin screen (the section the step
// is about), not the public detail page — that's where a leader actually
// edits the page, invites members, etc. Returns the original href when the
// user has no leader group of the relevant kind (the directory list is then
// the right fallback).
export function patchChecklistHref(
  key: ActionKey,
  baseHref: string,
  ctx: {
    readonly leaderCircleId?: string | null;
    readonly leaderNetworkId?: string | null;
    readonly hubNetworkId?: string | null;
    readonly meetingGuideResourceId?: string | null;
  },
): string {
  // The Network Leaders Hub is a specific network (its id is per-environment
  // config, surfaced on /me/onboarding-progress). Fall back to the directory
  // when unconfigured.
  if (key === 'visit_hub' && ctx.hubNetworkId != null) {
    return `/networks/${ctx.hubNetworkId}?tab=overview`;
  }
  // Same shape for the first-meeting guide: a specific resource whose id is
  // per-environment config. Fall back to the library when unconfigured.
  if (key === 'plan_first_meeting' && ctx.meetingGuideResourceId != null) {
    return `/resources/${ctx.meetingGuideResourceId}`;
  }
  if (ctx.leaderCircleId != null) {
    if (key === 'complete_circle_profile')
      return `/circles/${ctx.leaderCircleId}/admin?section=overview`;
    if (key === 'invite_circle_members')
      return `/circles/${ctx.leaderCircleId}/admin?section=members&invite=true`;
  }
  if (ctx.leaderNetworkId != null) {
    if (key === 'update_network_page')
      return `/networks/${ctx.leaderNetworkId}/admin?section=overview`;
    if (key === 'invite_network_members')
      return `/networks/${ctx.leaderNetworkId}/admin?section=members&invite=true`;
    if (key === 'add_event')
      return `/events/new?groupId=${ctx.leaderNetworkId}`;
  }
  return baseHref;
}
